import { useState } from 'react';
import { Task } from '@/lib/types';
import { useTaskStore } from '@/lib/task-store';
import { Button } from '@/components/ui/button';
import { TASK_CATEGORIES, TASK_PRIORITIES, TASK_STATUSES } from '@/lib/constants/task';
import { DateInput } from '@/components/ui/date-input';
import { useOwnerStore } from '@/lib/owner-store';
import { useEffect } from 'react';
import { toLocal } from '@/lib/utils/timezone';

interface TaskEditFormProps {
  task: Task;
  onCancel: () => void;
  onSuccess: () => void;
}

export function TaskEditForm({ task, onCancel, onSuccess }: TaskEditFormProps) {
  const { updateTask } = useTaskStore();
  const { owners, fetchOwners } = useOwnerStore();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: task.title,
    description: task.description || '',
    category: task.category,
    status: task.status,
    priority: task.priority,
    owner_id: task.owner_id || '',
    date_assigned: toLocal(task.date_assigned) || '',
    due_date: toLocal(task.due_date) || '',
    notes: task.notes || ''
  });

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      console.log('Submitting update for task:', task.id);
      console.log('Form data:', formData);

      // Create update object with all fields
      const updates = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        status: formData.status,
        priority: formData.priority,
        owner_id: formData.owner_id || null,
        date_assigned: formData.date_assigned,
        due_date: formData.due_date,
        notes: formData.notes.trim()
      };

      console.log('Sending updates to Supabase:', updates);

      await updateTask(task.id, updates);
      console.log('Update successful');
      onSuccess();
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err instanceof Error ? err.message : 'Failed to update task');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    console.log('Field changed:', name, value);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Title</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          >
            {TASK_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          >
            {TASK_PRIORITIES.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          >
            {TASK_CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Date Assigned</label>
          <DateInput
            name="date_assigned"
            value={formData.date_assigned}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Due Date</label>
          <DateInput
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Owner</label>
        <select
          name="owner_id"
          value={formData.owner_id}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          required
        >
          <option value="">Select owner...</option>
          {owners.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name} {owner.organization ? `(${owner.organization})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}